import React, { useState, useEffect } from "react";
import CustomInputDatePicker from "../../../../../Common/CustomInputDatePicker";
import Grid from "utils/Grid";
import Box from "utils/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import TableChartIcon from "@mui/icons-material/TableChart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import Chip from "@mui/material/Chip";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from "@mui/material/TextField";
import TableGenerator from "../../../../../Common/TableGenerator/collapsTable";
import moment from "dayjs";
import _ from "lodash";
import { useSelector } from "react-redux";
import AsyncAutoComplete from "../common/AsyncAutoComplete";
import {
  convertUtcToLocal,
  dateFormatter,
  getFilterEndDate,
  getFilterStartDate,
  currencyFormatter,
} from "utils/helpers";
import MegaLineChart from "./MegaLinechart";
import { useSnackbar } from "notistack";
import { getTimeZoneDifference } from "utils/helpers";
import { ExportButton } from "components/Common/Buttons";
import styles from "./styles";

const Salesfields = [
  {
    key: "date",
    visible: true,
    columnName: "Date",
    render: (value) => dateFormatter(convertUtcToLocal(value)) ?? "---",
  },
  {
    key: "product_name",
    visible: true,
    columnName: "Name",
    disableSorting: true,
  },
  {
    key: "product_sku",
    visible: true,
    columnName: "SKU",
    render: (value) => (value && value.length > 0 ? value : "---"),
    disableSorting: true,
  },
  {
    key: "quantity",
    visible: true,
    columnName: "Quantity",
    disableSorting: true,
  },
  {
    key: "sales",
    visible: true,
    columnName: "Sales($)",
    render: (value) => (value ? value : "0.00"),
    disableSorting: true,
  },
];

const Profitfields = [
  {
    key: "date",
    visible: true,
    columnName: "Date",
    render: (value) => dateFormatter(convertUtcToLocal(value)) ?? "---",
  },
  {
    key: "product_name",
    visible: true,
    columnName: "Name",
    disableSorting: true,
  },
  {
    key: "product_sku",
    visible: true,
    columnName: "SKU",
    render: (value) => (value && value.length > 0 ? value : "---"),
    disableSorting: true,
  },
  {
    key: "quantity",
    visible: true,
    columnName: "Quantity",
    disableSorting: true,
  },
  {
    key: "profit",
    visible: true,
    columnName: "Profit($)",
    render: (value) => (value ? value : "0.00"),
    disableSorting: true,
  },
];

const BarGraph = ({ data = [], loading, enableTaxes = false }) => {
  const classes = styles();
  if (!data.length && !loading) {
    return (
      <div className={classes.chartNoData}>
        <div>No data for selected filters.</div>{" "}
      </div>
    );
  }
  return (
    <ResponsiveContainer width="102%" height="100%">
      <BarChart
        width={500}
        height={200}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" xAxisId={0} />
        <XAxis dataKey="date" xAxisId={1} hide />
        <XAxis dataKey="date" xAxisId={2} hide />
        <YAxis
        // label={{ value: "Sales and Profit ($)", angle: -90, position: "insideLeft" }}
        />
        <Tooltip formatter={(value) => currencyFormatter(value)} />
        {/* color: props.theme.palette.primary.main
                                  ? props.theme.palette.primary.main
                                  : hostConfig.themeColor, */}
        <Legend verticalAlign="top" align="right" />
        <Bar name="Sales" dataKey="total_sales" xAxisId={2} fill="#024B7A" />

        <Bar name="Profit" dataKey="total_profit" xAxisId={1} fill="#44B7C2" />
        {enableTaxes && (
          <Bar name="Tax" dataKey="total_tax" xAxisId={0} fill="#FFAE49" />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

function DashboardView() {
  const classes = styles();

  const operatorsData = useSelector(
    (state) => state.dropdownReducer.operatorsData
  );
  const areasData = useSelector((state) => state.dropdownReducer.areasData);
  const locationsData = useSelector(
    (state) => state.dropdownReducer.locationsData
  );
  const vmData = useSelector((state) => state.dropdownReducer.devicesData);
  const inventoryProductsData = useSelector(
    (state) => state.initialLoadReducer.inventoryData
  );
  const [loadingYearly, setLoadingYearly] = useState(false);
  const [isYearlyExist, setIsYearlyExist] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [isDailyExist, setIsDailyExist] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingProfit, setLoadingProfit] = useState(false);
  const [salesProfitData, setSalesProfitData] = useState({});
  const [salesProfitProductList, setSalesProfitProductList] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(15, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());

  /* Dropdown option list */
  const [locationList, setLocationList] = useState(locationsData || []);
  const [areaList, setAreaList] = useState(areasData || []);
  const [vmList, setVmList] = useState(vmData || []);

  const [filters, setFilters] = useState({
    operator: { value: "all", label: "All" },
    area: { value: "all", label: "All" },
    location: { value: "all", label: "All" },
    vm: [{ value: "all", label: "All" }],
  });
  /* Chart / Table data */
  const [dailyFinanceData, setDailyFinanceData] = useState([]);
  const [monthlyFinanceData, setMonthlyFinanceData] = useState([]);
  const [yearlyFinanceData, setYearlyFinanceData] = useState([]);
  const [salesRevenue, setSalesRevenue] = useState([]);
  const [salesProfit, setSalesProfit] = useState([]);

  /** Dropdown loading state */
  const [areaListLoading, setAreaListLoading] = useState(false);
  const [locationListLoading, setLocationListLoading] = useState(false);
  const [vmListLoading, setVmListLoading] = useState(false);

  /* Graph filter toggle */
  const [filterValue, setFilterValue] = useState("graph");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [defaultProductSet, setDefaultProductsSet] = useState([]);
  const [updatedProducts, setUpdatedProducts] = useState(defaultProductSet);
  const [more, setMore] = useState(false);
  const [ordering, setOrdering] = useState("date");

  /* Filters take effect only when Apply is clicked */
  const filterKey = JSON.stringify([
    moment(startDate).format("YYYY-MM-DD"),
    moment(endDate).format("YYYY-MM-DD"),
    filters.operator?.value,
    filters.area?.value,
    filters.location?.value,
    (filters.vm || []).map((x) => x.value),
  ]);
  const [appliedKey, setAppliedKey] = useState(filterKey);
  const hasPendingFilters = filterKey !== appliedKey;
  const enableTaxesPerVM = useSelector(
    (state) => state.companySettingsReducer.enableTaxesPerVM
  );

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const { enqueueSnackbar } = useSnackbar();
  const fields = [
    {
      key: "date",
      visible: true,
      columnName: "Date",
    },
    {
      key: "total_sales",
      visible: true,
      columnName: "Total Sales",
    },
    {
      key: "quantity",
      visible: true,
      columnName: "Volume",
    },
    {
      key: "total_profit",
      visible: true,
      columnName: "Profit",
    },
    {
      key: "total_tax",
      visible: enableTaxesPerVM,
      columnName: "Tax",
    },
  ];

  const dailyfields = [
    {
      key: "date",
      visible: true,
      columnName: "Date",
      render: (value) => dateFormatter(convertUtcToLocal(value)) ?? "---",
    },
    {
      key: "total_sales",
      visible: true,
      columnName: "Total Sales",
    },
    {
      key: "quantity",
      visible: true,
      columnName: "Volume",
    },
    {
      key: "total_profit",
      visible: true,
      columnName: "Profit",
    },
    {
      key: "total_tax",
      visible: enableTaxesPerVM,
      columnName: "Tax",
    },
  ];
  const fetchLocations = async (areaList = [], area_id, selectedOperator) => {
    try {
      setLocationListLoading(true);
      if (area_id && area_id !== "all") {
        const list =
          _.find(areaList, (x) => x.value === area_id)?.locations || [];

        setLocationList([
          { label: "All", value: "all" },
          ..._.map(list, ({ location_id, location_name }) => ({
            label: location_name,
            value: location_id,
          })),
        ]);
      } else {
        if (selectedOperator && selectedOperator?.value !== "all") {
          let newLocation = [];
          const locationData = [
            ..._.map(areaList, ({ locations }) =>
              Array.prototype.push.apply(newLocation, locations)
            ),
          ];
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(newLocation, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        } else {
          const { data } = await window.axiosIns("locations/locations", {
            params: {
              all: true,
              state: "all",
              ordering: "location_name",
              lite: true,
            },
          });
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(data?.results, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLocationListLoading(false);
    }
  };

  const fetchVM = async (selectedOperator, selectedArea, selectedLocation) => {
    try {
      setVmListLoading(true);
      setVmList([]);
      const params = {
        all: true,
        ordering: "vm_name",
        lite: true,
      };
      if (selectedOperator && selectedOperator.value !== "all")
        params.company_id = selectedOperator.value;
      if (selectedArea && selectedArea.value !== "all")
        params.area_id = selectedArea.value;
      if (selectedLocation && selectedLocation.value !== "all")
        params.location = selectedLocation.value;
      const { data } = await window.axiosIns("device", {
        params,
      });
      setVmList([
        { label: "All", value: "all" },
        ..._.map(data?.data?.results, ({ id, serial_number, vm_name }) => ({
          label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
          value: id,
        })),
      ]);
    } catch (err) {
      console.log("err", err);
      setVmListLoading(false);
    } finally {
      setVmListLoading(false);
    }
  };

  const handleBackDate = () => {
    setSelectedYear((prevState) => prevState - 1);
  };

  const handleNextDate = () => {
    setSelectedYear((prevState) => prevState + 1);
  };

  const fetchAreas = async (selectedOperator) => {
    try {
      setAreaListLoading(true);
      const params = { all: true, ordering: "area_name" };
      if (selectedOperator && selectedOperator.value !== "all")
        params.operator_id = selectedOperator.value;
      const { data } = await window.axiosIns("locations/areas", {
        params,
      });
      let areaList = [
        { label: "All", value: "all" },
        ..._.map(data?.results, ({ area_id, area_name, locations }) => ({
          label: area_name,
          value: area_id,
          locations,
        })),
      ];
      setAreaList(areaList);
      fetchLocations(areaList, null, selectedOperator);
    } catch (err) {
      console.log(err);
      fetchLocations([], null, selectedOperator);
    } finally {
      setAreaListLoading(false);
    }
  };
  const formatFinance = (rawData) => {
    return rawData.map((item) => {
      return {
        total_profit: parseFloat(currencyFormatter(item.total_profit)),
        total_sales: parseFloat(currencyFormatter(item.total_sales)),
        total_tax: parseFloat(currencyFormatter(item.total_tax)),
        quantity: item.quantity,
        date: item.date,
      };
    });
  };

  const fetchAnalytics = async (fetchFor = "all", order = null) => {
    try {
      const params = {
        start_date: getFilterStartDate(startDate),
        end_date: getFilterEndDate(endDate),
        ordering: order ? order : ordering,
        tz: getTimeZoneDifference(),
        _scope: "AND",
      };

      if (filters.vm[0]?.value && filters.vm[0]?.value !== "all") {
        params.device_ids = filters.vm
          .map((x) => x.value)
          .filter((x) => x !== "all")
          .join(",");
      }
      if (filters.location.value && filters.location.value !== "all") {
        params.location_id = filters.location.value;
      }
      if (filters.area.value && filters.area.value !== "all") {
        params.area_id = filters.area.value;
      }
      if (filters.operator.value && filters.operator.value !== "all") {
        params.operator_id = filters.operator.value;
      }

      if (fetchFor === "all") {
        setLoadingYearly(true);
        setLoadingDaily(true);
      } else if (fetchFor === "yearly") {
        setLoadingYearly(true);
      } else {
        setLoadingDaily(true);
      }

      const { data } = await window.axiosIns("finance_analytics/v2", {
        params,
      });
      const years = Object.keys(data?.monthly?.[0]);
      setSelectedYear(years[years.length - 1]);
      if (fetchFor === "daily" || fetchFor === "all") {
        const dailyFinance = _.map(data?.daily?.[0], (value, date) => ({
          ...value,
          total_profit: parseFloat(value.total_profit.toFixed(2)),
          total_sales: parseFloat(value.total_sales.toFixed(2)),
          total_tax: parseFloat(value.total_tax.toFixed(2)),
          date: dateFormatter(date),
        }));
        setIsDailyExist(data?.daily?.[1]);
        setDailyFinanceData(formatFinance(dailyFinance));
      }
      if (fetchFor === "yearly" || fetchFor === "all") {
        const yearlyFinance = _.map(
          data?.monthly?.[0]?.[years[years.length - 1]]?.[0],
          (value, date) => ({
            ...value,
            total_profit: parseFloat(value.total_profit.toFixed(2)),
            total_sales: parseFloat(value.total_sales.toFixed(2)),
            total_tax: parseFloat(value.total_tax.toFixed(2)),
            date: moment(date).format("MMM-YY"),
          })
        );

        setYearlyFinanceData(formatFinance(yearlyFinance));
        setIsYearlyExist(data?.monthly?.[1]);
      }
      setMonthlyFinanceData(data?.monthly);
    } catch (err) {
    } finally {
      setLoadingYearly(false);
      setLoadingDaily(false);
    }
  };

  const fetchSalesProfit = async (order = null, isProfit = null) => {
    let params = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      ordering: order ? order : ordering,
      tz: getTimeZoneDifference(),
    };
    if (filters.area.value && filters.area.value !== "all") {
      params.area_id = filters.area.value;
    }
    if (filters.location.value && filters.location.value !== "all") {
      params.location_id = filters.location.value;
    }
    if (filters.operator.value && filters.operator.value !== "all") {
      params.operator_id = filters.operator.value;
    }
    if (filters.vm[0]?.value && filters.vm[0]?.value !== "all") {
      params.device_id = filters.vm
        .map((x) => x.value)
        .filter((x) => x !== "all")
        .join(",");
    }

    try {
      const res = await window.axiosIns("inventory_statistics/sales_profit_v2", {
        params: params,
      });
      let products = res?.data?.products;
      let product_sales = res?.data?.product_sales;
      const arr =
        products?.map((product) => ({
          name: product.product_name,
          value: product.product_id,
        })) || [];
      setSalesProfitData(product_sales);
      setSalesProfitProductList(arr);
      if (isProfit === null) {
        setSalesProfit(preFormate(res?.data));
        setSalesRevenue(preFormate(res?.data));
      } else if (isProfit) {
        setSalesProfit(preFormate(res?.data));
      } else {
        setSalesRevenue(preFormate(res?.data));
      }
      setLoadingProfit(false);
      setLoadingRevenue(false);
    } catch (error) {
      enqueueSnackbar("Unable to fetch Revenue and Profit data.");
      setLoadingProfit(false);
      setLoadingRevenue(false);
    }
  };

  useEffect(() => {
    setYearlyFinanceData(
      _.map(monthlyFinanceData?.[0]?.[selectedYear]?.[0], (value, date) => ({
        ...value,
        date: moment(date).format("MMM-YY"),
      }))
    );
  }, [selectedYear]);

  const preFormate = (rawData) => {
    let data = [];
    try {
      for (const [key, value] of Object.entries(rawData.product_sales)) {
        if (value.length > 0) {
          value.map((val) => {
            let obj = {};
            obj.date = key;
            obj.product_name = val.product_name || "";
            obj.product_sku = val.product_sku || "";
            obj.quantity = val.quantity || "";
            obj.sales = val.sales || "";
            obj.profit = val.profit || "";
            data.push(obj);
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
    return data;
  };

  useEffect(() => {
    fetchDefaultProducts();
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchSalesProfit();
  }, [appliedKey]);

  const handleOperator = (val) => {
    updateFilter("operator", val);
    fetchAreas(val);
    fetchVM(val);
    updateFilter("area", { value: "all", label: "All" });
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", [{ value: "all", label: "All" }]);
  };

  const handleArea = (val) => {
    updateFilter("area", val);
    fetchLocations(areaList, val.value, filters.operator);
    fetchVM(filters.operator, val);
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", [{ value: "all", label: "All" }]);
  };

  const handleLocation = (val) => {
    updateFilter("location", val);
    fetchVM(filters.operator, filters.area, val);
    updateFilter("vm", [{ value: "all", label: "All" }]);
  };

  const prepareData = (products) => {
    if (Array.isArray(products) && products.length > 0) {
      return products.map((product) => ({
        name: product.product_name,
        value: product.product_id,
      }));
    }
    return [];
  };

  const fetchDefaultProducts = async () => {
    try {
      const { data } = await window.axiosIns(
        "inventory_statistics/sales_default",
        {
          params: {
            all: true,
          },
        }
      );
      let result = prepareData(
        data?.results?.[0]?.default_product_ids?.products
      );
      setDefaultProductsSet(result || []);
      setUpdatedProducts(result || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleExport = () => {
    let params = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference(),
      export: true,
    };
    if (filters.area.value && filters.area.value !== "all") {
      params.area_id = filters.area.value;
    }
    if (filters.location.value && filters.location.value !== "all") {
      params.location_id = filters.location.value;
    }
    if (filters.operator.value && filters.operator.value !== "all") {
      params.operator_id = filters.operator.value;
    }
    if (filters.vm[0]?.value && filters.vm[0]?.value !== "all") {
      params.device_ids = filters.vm
        .map((x) => x.value)
        .filter((x) => x !== "all")
        .join(",");
    }
    window
      .axiosIns("/finance_analytics", {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/json",
        },
        params: params,
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `VM-Sales-Report-${moment().format("MM-DD-YYYY")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        enqueueSnackbar("No Records available for selected date range.");
      });
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <Grid spacing={2} container className="mb-4">
        <div className={classes.selectHeader} style={{ display: "grid", gridTemplateColumns: "minmax(0,2.2fr) repeat(3,minmax(0,1fr)) minmax(0,1.6fr) 110px", gap: 12, width: "100%", alignItems: "center", boxSizing: "border-box" }}>
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <CustomInputDatePicker
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  label="Start Date"
                  maxDate={new Date(endDate).setDate(
                    new Date(endDate).getDate() - 1
                  )}
                  disabled={
                    areaListLoading || locationListLoading || vmListLoading
                  }
                />
              </div>
              <div>
                <CustomInputDatePicker
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  label="End Date"
                  minDate={new Date(startDate).setDate(
                    new Date(startDate).getDate() + 1
                  )}
                  maxDate={new Date()}
                  disabled={
                    areaListLoading || locationListLoading || vmListLoading
                  }
                />
              </div>
            </div>
          </div>

          {/* Operator Dropdown */}
          <div>
            <AsyncAutoComplete
              onChange={handleOperator}
              value={filters.operator}
              loading={false}
              options={operatorsData}
              label="Operator"
              disabled={areaListLoading || locationListLoading || vmListLoading}
            />
          </div>

          {/* Area Dropdown */}
          <div>
            <AsyncAutoComplete
              onChange={handleArea}
              loading={areaListLoading}
              value={filters.area}
              options={areaList}
              label="Area"
              disabled={areaListLoading || locationListLoading || vmListLoading}
            />
          </div>

          {/* Location Dropdown */}
          <div>
            <AsyncAutoComplete
              onChange={handleLocation}
              value={filters.location}
              loading={locationListLoading}
              options={locationList}
              label="Location"
              disabled={areaListLoading || locationListLoading || vmListLoading}
            />
          </div>

          <div className={classes.vmField}>
            <Autocomplete
              multiple
              style={{ width: "100%" }}
              id="tags-outlined"
              value={filters.vm}
              loading={vmListLoading}
              options={vmList || []}
              disabled={areaListLoading || locationListLoading || vmListLoading}
              defaultValue={[{ label: "All", value: "all" }]}
              onChange={(event, newValue) => {
                // Never allow an empty selection: fall back to "All"
                if (!newValue || newValue.length === 0) {
                  updateFilter("vm", [{ label: "All", value: "all" }]);
                  return;
                }
                const allPos = _.findIndex(newValue, (x) => x.value === "all");
                if (allPos === 0 && newValue?.length > 1) {
                  updateFilter(
                    "vm",
                    _.filter(newValue, (x) => x.value !== "all")
                  );
                } else if (allPos > 0) {
                  updateFilter(
                    "vm",
                    _.filter(newValue, (x) => x.value === "all")
                  );
                } else {
                  updateFilter("vm", newValue);
                }
              }}
              getOptionLabel={(option) => option?.label || ""}
              renderTags={(tagValue, getTagProps) =>
                tagValue.length < 2 ? (
                  tagValue.map((option, index) => {
                    const { key, ...tp } = getTagProps({ index });
                    return (
                      <Chip key={key}
                        {...tp}
                        size="medium"
                        label={`${option.label.slice(0, 8)}...`}
                      />
                    );
                  })
                ) : (
                  <>
                    <div>
                      {(more ? tagValue : tagValue.slice(0, 1)).map((option, index) => {
                        const { key, ...tp } = getTagProps({ index });
                        return (
                          <Chip key={key}
                            {...tp}
                            size="medium"
                            label={`${option.label.slice(0, 8)}...`}
                          />
                        );
                      })}
                      {!more && (
                        <span
                          style={{
                            position: "absolute",
                            fontSize: "13px",
                            top: "20px",
                            cursor: "pointer",
                          }}
                          onClick={() => setMore(!more)}
                        >{`+${tagValue.length - 1} more`}</span>
                      )}
                    </div>
                    {more && (
                      <span
                        style={{
                          position: "static",
                          paddingLeft: "10px",
                          fontSize: "13px",
                          top: "20px",
                          cursor: "pointer",
                        }}
                        onClick={() => setMore(!more)}
                      >
                        show less
                      </span>
                    )}
                  </>
                )
              }
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Vending Machines"
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  slotProps={{
                    ...params.slotProps, input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {vmListLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    }
                  }}
                />
              )}
            />
          </div>

          <div>
            <Button
              variant="contained"
              color="primary"
              style={{ width: "100%", height: 40 }}
              disabled={
                !hasPendingFilters ||
                areaListLoading ||
                locationListLoading ||
                vmListLoading
              }
              onClick={() => setAppliedKey(filterKey)}
            >
              Apply
            </Button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, width: "100%", padding: "0 1rem", marginTop: -4 }}>
          <ExportButton
            label={"Export"}
            style={{ minWidth: 100, height: 40 }}
            onClick={handleExport}
          />
          <ToggleButtonGroup
            size="small"
            value={filterValue}
            exclusive
            onChange={(_, value) => value && setFilterValue(value)}
          >
            <ToggleButton value="graph" style={{ height: 40, width: 64 }}>
              <AssessmentIcon />
            </ToggleButton>
            <ToggleButton value="table" style={{ height: 40, width: 64 }}>
              <TableChartIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Grid xs={6}>
          <Box>
            <Typography variant="h6">Revenue</Typography>
          </Box>
          {filterValue === "graph" ? (
            <MegaLineChart
              fetchSalesProfit={fetchSalesProfit}
              salesProfitData={salesProfitData}
              salesProfitProductList={salesProfitProductList}
              ChartTitle="Revenue"
              vaxisTitle="Revenue($)"
              startDate={startDate}
              endDate={endDate}
              selectedFilters={filters}
              loader={loadingRevenue}
              defaultProducts={prepareData(
                inventoryProductsData?.product_available
              )}
              defaultProductSet={defaultProductSet}
              updatedProducts={updatedProducts}
              setDefaultProductsSet={setDefaultProductsSet}
              setUpdatedProducts={setUpdatedProducts}
            />
          ) : (
            <Box className="mt-4">
              <TableGenerator
                showSelectAll={false}
                initialSort={"date"}
                sortOrder={"asc"}
                stickyFooter={true}
                stickyHeader
                tableHeight={410}
                disableMinWidth
                sensorTable={true}
                loader={loadingRevenue}
                backendPagination={false}
                rowOnePage={5}
                fields={Salesfields}
                data={salesRevenue}
                handleSortChange={(ordering) => {
                  setOrdering(ordering);
                  fetchSalesProfit(ordering, false);
                }}
                height={440}
                defaultPadding
                stickyTable
              />
            </Box>
          )}
        </Grid>
        <Grid xs={6}>
          <Box>
            <Typography variant="h6" style={{ display: "flex" }}>
              Monthly Sales
              <Box>
                <ButtonGroup
                  disableElevation
                  size="small"
                  color="primary"
                  style={{ position: "absolute", right: 50 }}
                >
                  <Button
                    disabled={selectedYear <= moment(startDate).year()}
                    onClick={() => handleBackDate()}
                  >
                    <NavigateBeforeIcon />
                  </Button>
                  <Button
                    disabled={selectedYear >= moment(endDate).year()}
                    onClick={() => handleNextDate()}
                  >
                    <NavigateNextIcon />
                  </Button>
                </ButtonGroup>
              </Box>
            </Typography>
          </Box>
          {filterValue === "graph" ? (
            <Box className={classes.graphContainer}>
              {loadingYearly && (
                <CircularProgress className={classes.noDataMessage} />
              )}

              {/** Loading end and the data is not there */}
              {!loadingYearly && !isYearlyExist && (
                <Typography className={classes.noDataMessage} variant="h6">
                  No Data
                </Typography>
              )}
              <BarGraph
                loading={loadingYearly}
                data={yearlyFinanceData}
                enableTaxes={enableTaxesPerVM}
              />
            </Box>
          ) : (
            <Box className="mt-4">
              <TableGenerator
                showSelectAll={false}
                initialSort={"date"}
                sortOrder={"asc"}
                stickyFooter={true}
                stickyHeader
                tableHeight={410}
                disableMinWidth
                sensorTable={true}
                loader={loadingYearly}
                backendPagination={false}
                rowOnePage={5}
                fields={fields}
                handleSortChange={(ordering) => {
                  setOrdering(ordering);
                  fetchAnalytics("yearly", ordering);
                }}
                data={yearlyFinanceData}
                height={440}
                defaultPadding
                stickyTable
              />
            </Box>
          )}
        </Grid>

        <Grid style={{ paddingTop: 0 }} xs={6}>
          <Box>
            <Typography variant="h6">Profit</Typography>
          </Box>
          {filterValue === "graph" ? (
            <MegaLineChart
              hideSettingIcon
              fetchSalesProfit={fetchSalesProfit}
              salesProfitData={salesProfitData}
              salesProfitProductList={salesProfitProductList}
              ChartTitle="Profit"
              vaxisTitle="Profit($)"
              startDate={startDate}
              endDate={endDate}
              selectedFilters={filters}
              loader={loadingProfit}
              defaultProducts={prepareData(
                inventoryProductsData?.product_available
              )}
              defaultProductSet={defaultProductSet}
              updatedProducts={updatedProducts}
              setDefaultProductsSet={setDefaultProductsSet}
              setUpdatedProducts={setUpdatedProducts}
            />
          ) : (
            <Box className="mt-4">
              <TableGenerator
                showSelectAll={false}
                initialSort={"date"}
                sortOrder={"asc"}
                stickyFooter={true}
                stickyHeader
                tableHeight={410}
                disableMinWidth
                sensorTable={true}
                loader={loadingProfit}
                backendPagination={false}
                rowOnePage={5}
                fields={Profitfields}
                data={salesProfit}
                handleSortChange={(ordering) => {
                  setOrdering(ordering);
                  fetchSalesProfit(ordering, true);
                }}
                height={440}
                defaultPadding
                stickyTable
              />
            </Box>
          )}
        </Grid>

        <Grid style={{ paddingTop: 0 }} xs={6}>
          <Box>
            <Typography variant="h6">Daily Sales</Typography>
          </Box>
          {filterValue === "graph" ? (
            <Box className={classes.graphContainer}>
              {loadingDaily && (
                <CircularProgress className={classes.noDataMessage} />
              )}

              {/** Loading end and the data is not there */}
              {!loadingDaily && !isDailyExist && (
                <Typography className={classes.noDataMessage} variant="h6">
                  No Data
                </Typography>
              )}
              <BarGraph
                loading={loadingDaily}
                data={dailyFinanceData}
                enableTaxes={enableTaxesPerVM}
              />
            </Box>
          ) : (
            <Box className="mt-4">
              <TableGenerator
                showSelectAll={false}
                initialSort={"date"}
                sortOrder={"asc"}
                stickyHeader
                stickyFooter={true}
                tableHeight={410}
                disableMinWidth
                sensorTable={true}
                backendPagination={false}
                loader={loadingDaily}
                rowOnePage={5}
                fields={dailyfields}
                handleSortChange={(ordering) => {
                  setOrdering(ordering);
                  fetchAnalytics("daily", ordering);
                }}
                data={dailyFinanceData}
                defaultPadding
                stickyTable
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default DashboardView;
